/* StampToggle — LED-02, LED-03 */
import focus from '../primitives/focus.module.css';
import styles from './StampToggle.module.css';

export type EntryKind = 'debit' | 'credit';

export interface StampToggleProps {
  readonly value: EntryKind;
  readonly onChange: (value: EntryKind) => void;
  /** Names the pair for a screen reader. Defaults to what `LED-02` calls it. */
  readonly label?: string;
  readonly disabled?: boolean;
}

/**
 * Debit or credit, on `LED-02`.
 *
 * **Two radio buttons, not a switch.** A switch says on-or-off, and these are two named things of equal
 * standing — a screen reader saying "credit, off" would be describing a state nobody is in. So this is a
 * radio group: one tab stop, arrow keys between the two, and each option announced by its own name.
 *
 * Debit is preselected by the screen that uses it, not by this component: `LED-02` says "nine entries in
 * ten", which is a fact about the screen rather than about the control, and a component with an opinion
 * about its own initial value is one a second screen cannot reuse.
 */
export function StampToggle({
  value,
  onChange,
  label = 'Entry type',
  disabled = false,
}: StampToggleProps): React.JSX.Element {
  const options: readonly { kind: EntryKind; sign: string; text: string }[] = [
    { kind: 'debit', sign: '\u2212', text: 'Debit' },
    { kind: 'credit', sign: '+', text: 'Credit' },
  ];

  return (
    <div className={styles.stamps} role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const selected = option.kind === value;

        return (
          <button
            key={option.kind}
            type="button"
            role="radio"
            aria-checked={selected}
            // Only the selected option is in the tab order, which is what a radio group does: one stop for
            // the pair, then arrows. Two tab stops would make this a pair of buttons wearing radio clothes.
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              onChange(option.kind);
            }}
            onKeyDown={(event) => {
              if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
                event.preventDefault();
                onChange(option.kind === 'debit' ? 'credit' : 'debit');
              }
            }}
            className={[
              styles.stamp,
              styles[option.kind],
              selected ? styles.on : '',
              focus.focusable,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className={styles.sign} aria-hidden="true">
              {option.sign}
            </span>
            <span className={styles.text}>{option.text}</span>
          </button>
        );
      })}
    </div>
  );
}
